import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import TagsService, { type CreateTagInput, type GetTagsParams, type UpdateTagInput } from '~/services/TagsService';

export function useGetTags(params: GetTagsParams) {
  return useQuery({
    queryKey: ['tags', params.offset, params.limit, params.sortBy, params.sortOrder],
    queryFn: () => TagsService.getTags(params),
  });
}

export function useGetTag(tagId: string) {
  return useQuery({
    queryKey: ['tags', tagId],
    queryFn: () => TagsService.getTag(tagId),
    enabled: !!tagId,
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (data: CreateTagInput) => TagsService.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      enqueueSnackbar('Tag created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating tag: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTagInput }) => TagsService.updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      enqueueSnackbar('Tag updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating tag: ${error.message}`, { variant: 'error' });
    },
  });
}
